import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import { reachOf } from '../utils/reportMetrics';
import './CreatorStreamHistory.css';

const CreatorStreamHistory = ({ creatorId, token: propToken, selectedPlatform = null, selectedContentType: propContentType = 'all', showAllPlatforms = false, activePlatforms = [] }) => {
  const [content, setContent] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('streams');
  const [filterDays, setFilterDays] = useState(30);
  const [filterContentType, setFilterContentType] = useState(propContentType);
  const [expandedContent, setExpandedContent] = useState(new Set());
  const [currentPlatform, setCurrentPlatform] = useState(selectedPlatform || 'twitch');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [sortField, setSortField] = useState('postDate');
  const [sortDir, setSortDir] = useState('desc');

  const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;
  const token = propToken || localStorage.getItem('token');

  useEffect(() => {
    if (!creatorId || !token) {
      setError('Missing creator ID or authentication token');
      setLoading(false);
      return;
    }
    if (selectedPlatform) setCurrentPlatform(selectedPlatform);
    if (propContentType) setFilterContentType(propContentType);
    fetchContentData();
    // Refresh every 5 minutes, not 30 seconds. This call returns the creator's
    // ENTIRE content history with segments and game rows (3.5 MB for the
    // heaviest creators), and the numbers it shows only change when the
    // 5-minute live-monitoring cycle writes new samples — so polling faster
    // than that re-downloaded identical data ten times over.
    const refreshInterval = setInterval(() => fetchContentData(), 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [creatorId, filterDays, token, selectedPlatform, propContentType]);

  const fetchContentData = async () => {
    try {
      const response = await axios.get(`${API_BASE}/creators/${creatorId}`, { headers: { Authorization: `Bearer ${token}` } });
      const rawContent = response.data.creator?.content || response.data.creator?.Content || [];
      const creatorContent = rawContent.map(item => ({
        ...item,
        segments: item.segments || item.ContentSegment?.map(seg => ({ ...seg, game: seg.game || seg.Game })) || [],
        game: item.game || item.Game
      }));

      const contentMap = new Map();
      creatorContent.forEach(item => {
        const key = `${item.title}|${item.platform}|${item.postDate}`;
        const existing = contentMap.get(key);
        if (existing) {
          if (!existing.streamDuration && item.streamDuration) contentMap.set(key, item);
          else if ((item.segments?.length || 0) > (existing.segments?.length || 0)) contentMap.set(key, item);
        } else contentMap.set(key, item);
      });

      let platformFiltered;
      if (showAllPlatforms) {
        platformFiltered = Array.from(contentMap.values());
      } else {
        const platformToFilter = selectedPlatform || currentPlatform;
        platformFiltered = Array.from(contentMap.values()).filter(item =>
          item.platform?.toLowerCase() === platformToFilter.toLowerCase()
        );
      }

      setContent(platformFiltered);
      calculatePerformanceByGame(platformFiltered);
      calculateDailyStats(platformFiltered);
      calculateTrending(platformFiltered);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching content:', err);
      setLoading(false);
    }
  };

  const calculatePerformanceByGame = (contentList) => {
    const gameStats = new Map();
    contentList.forEach(stream => {
      if (stream.segments?.length > 0) {
        stream.segments.forEach(segment => {
          const gameId = segment.game?.id || segment.game?.title;
          if (!gameId) return;
          if (!gameStats.has(gameId)) gameStats.set(gameId, { game: segment.game, totalStreams: 0, totalMinutes: 0, avgCcvValues: [], peakCcvValues: [] });
          const stats = gameStats.get(gameId);
          stats.totalStreams += 1;
          stats.totalMinutes += segment.durationMinutes || 0;
          if (segment.avgCcv > 0) stats.avgCcvValues.push(segment.avgCcv);
          if (segment.peakCcv > 0) stats.peakCcvValues.push(segment.peakCcv);
        });
      }
    });
    setPerformance(Array.from(gameStats.values()).map(g => ({
      game: g.game,
      metrics: {
        totalStreams: g.totalStreams,
        avgCcv: g.avgCcvValues.length > 0 ? Math.round(g.avgCcvValues.reduce((a, b) => a + b) / g.avgCcvValues.length) : 0,
        peakCcv: g.peakCcvValues.length > 0 ? Math.max(...g.peakCcvValues) : 0,
        totalMinutes: g.totalMinutes
      }
    })).sort((a, b) => b.metrics.avgCcv - a.metrics.avgCcv));
  };

  const calculateDailyStats = (contentList) => {
    const dailyMap = new Map();
    contentList.forEach(stream => {
      const dateKey = new Date(stream.postDate).toISOString().split('T')[0];
      if (!dailyMap.has(dateKey)) dailyMap.set(dateKey, { statDate: dateKey, streamCount: 0, avgCcvValues: [], peakCcvValues: [], totalWatchHours: 0, games: new Set() });
      const daily = dailyMap.get(dateKey);
      daily.streamCount += 1;
      if (stream.streamAvgCcv > 0) daily.avgCcvValues.push(stream.streamAvgCcv);
      if (stream.streamPeakCcv > 0) daily.peakCcvValues.push(stream.streamPeakCcv);
      if (stream.streamWatchHours) daily.totalWatchHours += stream.streamWatchHours;
      if (stream.segments) stream.segments.forEach(seg => { if (seg.game?.title) daily.games.add(seg.game.title); });
    });
    setDailyStats(Array.from(dailyMap.values()).map(d => ({
      id: d.statDate, statDate: d.statDate, streamCount: d.streamCount,
      avgCcv: d.avgCcvValues.length > 0 ? Math.round(d.avgCcvValues.reduce((a, b) => a + b) / d.avgCcvValues.length) : 0,
      peakCcv: d.peakCcvValues.length > 0 ? Math.max(...d.peakCcvValues) : 0,
      watchHours: Math.round(d.totalWatchHours), games: Array.from(d.games)
    })).sort((a, b) => new Date(b.statDate) - new Date(a.statDate)));
  };

  const calculateTrending = (contentList) => {
    const now = new Date();
    const calc = (streams, startDate) => {
      const filtered = streams.filter(s => new Date(s.postDate) >= startDate);
      const avgCcvValues = filtered.map(s => s.streamAvgCcv || 0).filter(v => v > 0);
      const peakCcvValues = filtered.map(s => s.streamPeakCcv || 0).filter(v => v > 0);
      const watchHours = filtered.reduce((sum, s) => sum + (s.streamWatchHours || 0), 0);
      const views = filtered.map(s => reachOf(s)).filter(v => v > 0);
      const daysDiff = Math.max(1, Math.ceil((now - startDate) / 86400000));
      return {
        totalStreams: filtered.length,
        avgCcv: avgCcvValues.length > 0 ? Math.round(avgCcvValues.reduce((a, b) => a + b) / avgCcvValues.length) : 0,
        peakCcv: peakCcvValues.length > 0 ? Math.max(...peakCcvValues) : 0,
        totalWatchHours: watchHours,
        totalViews: views.reduce((a, b) => a + b, 0),
        avgStreamsPerDay: filtered.length / daysDiff
      };
    };
    setTrending([
      { period: 'Last 7 Days', metrics: calc(contentList, new Date(now.getTime() - 7 * 86400000)) },
      { period: 'Last 30 Days', metrics: calc(contentList, new Date(now.getTime() - 30 * 86400000)) },
      { period: 'Last 90 Days', metrics: calc(contentList, new Date(now.getTime() - 90 * 86400000)) },
    ]);
  };

  const toggleSegments = (contentId) => {
    const newExpanded = new Set(expandedContent);
    if (newExpanded.has(contentId)) newExpanded.delete(contentId); else newExpanded.add(contentId);
    setExpandedContent(newExpanded);
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatDateTime = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="sort-header"
      onClick={() => handleSort(field)}
    >
      <span>{children}</span>
      {sortField === field && <span className="sort-arrow">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  // Filter content by content type and date range
  const filteredContent = useMemo(() => {
    let filtered = content;
    if (isCustomRange && customDateFrom) {
      const from = new Date(customDateFrom);
      const to = customDateTo ? new Date(customDateTo + 'T23:59:59') : new Date();
      filtered = filtered.filter(item => { const d = item.postDate ? new Date(item.postDate) : null; return d && d >= from && d <= to; });
    } else {
      const cutoff = new Date(Date.now() - filterDays * 86400000);
      filtered = filtered.filter(item => { const d = item.postDate ? new Date(item.postDate) : null; return d && d >= cutoff; });
    }
    if (filterContentType !== 'all') {
      filtered = filtered.filter(item => {
        if (filterContentType === 'streams') return item.contentType === 'stream' || item.contentType === 'vod';
        if (filterContentType === 'videos') return item.contentType === 'video' || item.contentType === 'short' || !item.contentType;
        return true;
      });
    }
    return filtered;
  }, [content, filterContentType, filterDays, isCustomRange, customDateFrom, customDateTo]);

  // Sort content
  const sortedContent = useMemo(() => {
    const sorted = [...filteredContent];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'postDate': aVal = new Date(a.postDate || 0); bVal = new Date(b.postDate || 0); break;
        case 'views': aVal = reachOf(a); bVal = reachOf(b); break;
        case 'avgCcv': aVal = a.streamAvgCcv || 0; bVal = b.streamAvgCcv || 0; break;
        case 'peakCcv': aVal = a.streamPeakCcv || 0; bVal = b.streamPeakCcv || 0; break;
        case 'watchHours': aVal = a.streamWatchHours || 0; bVal = b.streamWatchHours || 0; break;
        case 'likes': aVal = a.likes || 0; bVal = b.likes || 0; break;
        case 'engagement': aVal = a.engagementRate || 0; bVal = b.engagementRate || 0; break;
        default: aVal = new Date(a.postDate || 0); bVal = new Date(b.postDate || 0);
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredContent, sortField, sortDir]);

  // Chart data for daily tab
  const dailyChartData = useMemo(() => {
    return [...dailyStats].reverse().slice(-30).map(d => ({
      date: new Date(d.statDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      avgCcv: d.avgCcv,
      peakCcv: d.peakCcv,
      watchHours: d.watchHours,
    }));
  }, [dailyStats]);

  // Twitch and Kick share the CCV/watch-hours layout (both are stream-only platforms).
  const isTwitchPlatform = showAllPlatforms ? false : ['twitch', 'kick'].includes(currentPlatform?.toLowerCase());
  const isYouTube = showAllPlatforms ? false : currentPlatform?.toLowerCase() === 'youtube';
  const isTikTok = showAllPlatforms ? false : currentPlatform?.toLowerCase() === 'tiktok';
  const contentLabel = showAllPlatforms ? 'Content' : (isYouTube || isTikTok ? 'Videos' : 'Streams');

  const platformLabel = (p) => {
    if (p === 'youtube') return 'YouTube';
    if (p === 'tiktok') return 'TikTok';
    if (p === 'twitch') return 'Twitch';
    if (p === 'kick') return 'Kick';
    return p?.charAt(0).toUpperCase() + p?.slice(1);
  };

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip-label">{label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }} className="chart-tooltip-value">
            {entry.name}: {entry.value?.toLocaleString() || 0}
          </p>
        ))}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading stream data...</div>;

  return (
    <div className="creator-stream-history">
      <div className="tabs">
        <button className={`tab ${activeTab === 'streams' ? 'active' : ''}`} onClick={() => setActiveTab('streams')}>
          {contentLabel} ({filteredContent.length})
        </button>
        {isTwitchPlatform && (
          <button className={`tab ${activeTab === 'performance' ? 'active' : ''}`} onClick={() => setActiveTab('performance')}>
            Performance by Game
          </button>
        )}
        <button className={`tab ${activeTab === 'trending' ? 'active' : ''}`} onClick={() => setActiveTab('trending')}>
          Trending
        </button>
        <button className={`tab ${activeTab === 'daily' ? 'active' : ''}`} onClick={() => setActiveTab('daily')}>
          Daily Summary
        </button>
      </div>

      <div className="filter-controls">
        <div className="time-range-pills">
          {[7, 14, 30, 60, 90].map(d => (
            <button key={d} className={`time-pill ${!isCustomRange && filterDays === d ? 'active' : ''}`} onClick={() => { setFilterDays(d); setIsCustomRange(false); }}>
              {d}d
            </button>
          ))}
          <button className={`time-pill ${isCustomRange ? 'active' : ''}`} onClick={() => setIsCustomRange(true)}>Custom</button>
        </div>
        {isCustomRange && (
          <div className="custom-date-inputs">
            <input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} />
            <span>to</span>
            <input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} />
          </div>
        )}
        {(!propContentType || propContentType === 'all') && (
          <select value={filterContentType} onChange={(e) => setFilterContentType(e.target.value)} style={{ marginLeft: '4px' }}>
            <option value="all">All Content</option>
            <option value="streams">Streams/VODs</option>
            <option value="videos">Videos</option>
          </select>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ========== STREAMS/VIDEOS TABLE ========== */}
      {activeTab === 'streams' && (
        <div className="streams-view">
          {sortedContent.length === 0 ? (
            <div className="empty-state">No {filterContentType === 'streams' ? 'streams' : filterContentType === 'videos' ? 'videos' : 'content'} found</div>
          ) : (
            <div className="content-table-wrapper">
              <table className="content-table">
                <thead>
                  <tr>
                    <th className="th-title">Title</th>
                    {showAllPlatforms && <th>Platform</th>}
                    <SortHeader field="postDate">Date</SortHeader>
                    {showAllPlatforms ? (
                      <>
                        <SortHeader field="views">Views</SortHeader>
                        <SortHeader field="avgCcv">Avg CCV</SortHeader>
                        <SortHeader field="likes">Likes</SortHeader>
                        <SortHeader field="engagement">Eng %</SortHeader>
                      </>
                    ) : isTwitchPlatform ? (
                      <>
                        <SortHeader field="avgCcv">Avg CCV</SortHeader>
                        <SortHeader field="peakCcv">Peak CCV</SortHeader>
                        <SortHeader field="watchHours">Watch Hrs</SortHeader>
                      </>
                    ) : isTikTok ? (
                      <>
                        <SortHeader field="views">Views</SortHeader>
                        <SortHeader field="likes">Likes</SortHeader>
                      </>
                    ) : (
                      <>
                        <SortHeader field="views">Views</SortHeader>
                        <SortHeader field="likes">Likes</SortHeader>
                        <SortHeader field="engagement">Eng %</SortHeader>
                      </>
                    )}
                    <th className="th-link"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContent.map((item) => {
                    const hasSegments = item.segments && item.segments.length > 0;
                    const isExpanded = expandedContent.has(item.id);
                    const isLive = item.isLive === true;
                    const isTw = item.platform?.toLowerCase() === 'twitch';
                    const colCount = showAllPlatforms ? 7 : (isTwitchPlatform ? 6 : isTikTok ? 4 : 5);

                    return (
                      <React.Fragment key={item.id}>
                        <tr
                          className={`content-row ${isLive ? 'row-live' : ''} ${hasSegments ? 'row-expandable' : ''}`}
                          onClick={() => hasSegments && toggleSegments(item.id)}
                        >
                          <td className="td-title">
                            <div className="title-cell">
                              {hasSegments && <span className={`row-expand-icon ${isExpanded ? 'expanded' : ''}`}>▾</span>}
                              {isLive && <span className="row-live-badge">LIVE</span>}
                              <span className="row-title-text">{item.title || 'Untitled'}</span>
                            </div>
                          </td>
                          {showAllPlatforms && <td className="td-platform"><span className="platform-pill" data-platform={item.platform?.toLowerCase()}>{platformLabel(item.platform)}</span></td>}
                          <td className="td-date">{item.postDate ? formatDate(item.postDate) : '-'}</td>
                          {showAllPlatforms ? (
                            <>
                              <td className="td-metric">{(reachOf(item)).toLocaleString()}</td>
                              <td className="td-metric">{isTw ? (item.streamAvgCcv || 0).toLocaleString() : '-'}</td>
                              <td className="td-metric">{(item.likes || 0).toLocaleString()}</td>
                              <td className="td-metric">{item.engagementRate ? `${item.engagementRate.toFixed(2)}%` : '-'}</td>
                            </>
                          ) : isTwitchPlatform ? (
                            <>
                              <td className="td-metric">{(item.streamAvgCcv || 0).toLocaleString()}</td>
                              <td className="td-metric">{(item.streamPeakCcv || 0).toLocaleString()}</td>
                              <td className="td-metric">{(item.streamWatchHours || 0).toLocaleString()}</td>
                            </>
                          ) : isTikTok ? (
                            <>
                              <td className="td-metric">{(reachOf(item)).toLocaleString()}</td>
                              <td className="td-metric">{(item.likes || 0).toLocaleString()}</td>
                            </>
                          ) : (
                            <>
                              <td className="td-metric">{(reachOf(item)).toLocaleString()}</td>
                              <td className="td-metric">{(item.likes || 0).toLocaleString()}</td>
                              <td className="td-metric">{(item.engagementRate || 0).toFixed(2)}%</td>
                            </>
                          )}
                          <td className="td-link">
                            {item.contentUrl && (
                              <a href={item.contentUrl} target="_blank" rel="noopener noreferrer" className="view-link" onClick={(e) => e.stopPropagation()}>→</a>
                            )}
                          </td>
                        </tr>
                        {hasSegments && isExpanded && (
                          <tr className="segments-row">
                            <td colSpan={colCount}>
                              <div className="segments-section">
                                <div className="segments-title">Game Breakdown ({item.segments.length} segments)</div>
                                <div className="segments-list">
                                  {item.segments.map((segment, idx) => (
                                    <div key={segment.id} className="segment-item">
                                      <div className="segment-number">{idx + 1}</div>
                                      <div className="segment-image">
                                        {segment.game?.coverImageUrl ? (
                                          <img src={segment.game.coverImageUrl} alt={segment.game.title || 'Game'} />
                                        ) : (
                                          <div className="game-placeholder">🎮</div>
                                        )}
                                      </div>
                                      <div className="segment-details">
                                        <h5>{segment.game?.title || 'Unknown Game'}</h5>
                                        <div className="segment-meta">
                                          <span className="segment-duration">{formatDuration(segment.durationMinutes)}</span>
                                          {segment.avgCcv > 0 && <span className="segment-stat">Avg CCV: {segment.avgCcv.toLocaleString()}</span>}
                                          {segment.peakCcv > 0 && <span className="segment-stat">Peak: {segment.peakCcv.toLocaleString()}</span>}
                                          {(segment.watchHours > 0 || (segment.avgCcv > 0 && segment.durationMinutes > 0)) && (
                                            <span className="segment-stat">Watch Hrs: {(segment.watchHours || Math.round((segment.avgCcv * segment.durationMinutes) / 60)).toLocaleString()}</span>
                                          )}
                                        </div>
                                        {segment.game?.genres?.length > 0 && (
                                          <div className="segment-genres">
                                            {segment.game.genres.map((genre, i) => <span key={i} className="genre-tag">{genre}</span>)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {isYouTube && (
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '12px', paddingLeft: '4px' }}>
              YouTube data provided via YouTube Data API. Data is refreshed every 30 days.
            </p>
          )}
        </div>
      )}

      {/* ========== PERFORMANCE BY GAME ========== */}
      {activeTab === 'performance' && (
        <div className="performance-view">
          {performance.length === 0 ? (
            <div className="empty-state">No performance data</div>
          ) : (
            <div className="performance-grid">
              {performance.map((game, idx) => (
                <div key={idx} className="performance-card">
                  {game.game?.coverImageUrl && <img src={game.game.coverImageUrl} alt={game.game.title} className="game-cover" />}
                  <div className="game-info">
                    <h3>{game.game?.title || 'Unknown Game'}</h3>
                    {game.game?.genres && <p className="genres">{game.game.genres.join(', ')}</p>}
                  </div>
                  <div className="performance-metrics">
                    <div className="perf-metric"><span>Streams</span><strong>{game.metrics.totalStreams}</strong></div>
                    <div className="perf-metric"><span>Avg CCV</span><strong>{game.metrics.avgCcv.toLocaleString()}</strong></div>
                    <div className="perf-metric"><span>Peak CCV</span><strong>{game.metrics.peakCcv.toLocaleString()}</strong></div>
                    <div className="perf-metric"><span>Total Hours</span><strong>{formatDuration(game.metrics.totalMinutes)}</strong></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== TRENDING ========== */}
      {activeTab === 'trending' && (
        <div className="trending-view">
          {trending.length === 0 ? (
            <div className="empty-state">No trending data</div>
          ) : (
            <div className="trending-grid">
              {trending.map((period, idx) => (
                <div key={idx} className="trending-card">
                  <h3>{period.period}</h3>
                  <div className="trend-metrics">
                    {isTwitchPlatform ? (
                      <>
                        <div className="trend-item"><span className="label">Avg CCV</span><span className="value">{period.metrics.avgCcv.toLocaleString()}</span></div>
                        <div className="trend-item"><span className="label">Peak CCV</span><span className="value">{period.metrics.peakCcv.toLocaleString()}</span></div>
                        <div className="trend-item"><span className="label">Total Streams</span><span className="value">{period.metrics.totalStreams}</span></div>
                        <div className="trend-item"><span className="label">Streams/Day</span><span className="value">{period.metrics.avgStreamsPerDay.toFixed(1)}</span></div>
                        <div className="trend-item"><span className="label">Watch Hours</span><span className="value">{(period.metrics.totalWatchHours / 60).toFixed(1)}h</span></div>
                      </>
                    ) : (
                      <>
                        <div className="trend-item"><span className="label">Total {contentLabel}</span><span className="value">{period.metrics.totalStreams}</span></div>
                        <div className="trend-item"><span className="label">Total Views</span><span className="value">{period.metrics.totalViews.toLocaleString()}</span></div>
                        <div className="trend-item"><span className="label">{contentLabel}/Day</span><span className="value">{period.metrics.avgStreamsPerDay.toFixed(1)}</span></div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== DAILY SUMMARY ========== */}
      {activeTab === 'daily' && (
        <div className="daily-view">
          {/* Daily chart */}
          {dailyChartData.length > 1 && isTwitchPlatform && (
            <div className="daily-chart-container">
              <h3 className="daily-chart-title">Daily Avg CCV</h3>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={dailyChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} tickLine={false} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} interval={Math.max(0, Math.floor(dailyChartData.length / 6) - 1)} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="avgCcv" fill="#ec4899" radius={[3, 3, 0, 0]} name="Avg CCV" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {dailyStats.length === 0 ? (
            <div className="empty-state">No daily data</div>
          ) : (
            <div className="daily-table-wrapper">
              <table className="content-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Streams</th>
                    {isTwitchPlatform && <th>Avg CCV</th>}
                    {isTwitchPlatform && <th>Peak CCV</th>}
                    <th>Watch Hours</th>
                    <th>Games</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyStats.map((day) => (
                    <tr key={day.id} className="content-row">
                      <td className="td-date">{new Date(day.statDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td className="td-metric">{day.streamCount}</td>
                      {isTwitchPlatform && <td className="td-metric">{day.avgCcv?.toLocaleString() || '-'}</td>}
                      {isTwitchPlatform && <td className="td-metric">{day.peakCcv?.toLocaleString() || '-'}</td>}
                      <td className="td-metric">{day.watchHours?.toLocaleString() || 0}</td>
                      <td className="td-games">{day.games?.join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreatorStreamHistory;